import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { documentAPI } from '../api/api';
import { DAVA_TURU_LABELS } from '../utils/constants';
import { formatDateForDisplay } from '../utils/dateUtils';
import { downloadBlob } from '../utils/fileDownload';
import './Makbuzlar.css';
import { onayla } from '../utils/onayla';

const IbranameMuvafakatnameler = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('uploadDate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const allDocuments = await documentAPI.getIbranameMuvafakatnameler();
      setDocuments(Array.isArray(allDocuments) ? allDocuments : []);
    } catch (error) {
      console.error('Error loading documents:', error);
      alert('Belgeler yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const normalizeSearch = (value) => {
    if (!value) return '';
    return String(value)
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .trim();
  };

  const getFoy = (doc) => doc.CaseFileFoyNo ?? doc.caseFileFoyNo ?? '';
  const getMuvekkil = (doc) => doc.CaseFileMuvekkil ?? doc.caseFileMuvekkil ?? '';
  const getDavaTuruLabel = (doc) => {
    const davaTuru = doc.CaseFileDavaTuru ?? doc.caseFileDavaTuru;
    if (davaTuru == null) return '';
    return DAVA_TURU_LABELS[davaTuru] || '';
  };
  const getFileName = (doc) => doc.FileName ?? doc.fileName ?? '';
  const getCreatedDate = (doc) => doc.CreatedDate ?? doc.createdDate;
  const getUploadedBy = (doc) => doc.UploadedByUserName ?? doc.uploadedByUserName ?? '';
  const getDescription = (doc) => doc.Description ?? doc.description ?? '';

  const filteredDocuments = useMemo(() => {
    const q = normalizeSearch(searchTerm);
    if (!q) return documents;

    return documents.filter((doc) => {
      const haystack = normalizeSearch(
        `${getFoy(doc)} ${getMuvekkil(doc)} ${getDavaTuruLabel(doc)} ${getFileName(doc)} ${getDescription(doc)}`
      );
      return haystack.includes(q);
    });
  }, [documents, searchTerm]);

  const sortedDocuments = useMemo(() => {
    const list = [...filteredDocuments];
    const dir = sortDirection === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortColumn) {
        case 'foyNo':
          aVal = parseInt(getFoy(a), 10) || 0;
          bVal = parseInt(getFoy(b), 10) || 0;
          return (aVal - bVal) * dir;
        case 'muvekkil':
          aVal = normalizeSearch(getMuvekkil(a));
          bVal = normalizeSearch(getMuvekkil(b));
          return aVal.localeCompare(bVal, 'tr') * dir;
        case 'davaTuru':
          aVal = normalizeSearch(getDavaTuruLabel(a));
          bVal = normalizeSearch(getDavaTuruLabel(b));
          return aVal.localeCompare(bVal, 'tr') * dir;
        case 'fileName':
          aVal = normalizeSearch(getFileName(a));
          bVal = normalizeSearch(getFileName(b));
          return aVal.localeCompare(bVal, 'tr') * dir;
        case 'uploadedBy':
          aVal = normalizeSearch(getUploadedBy(a));
          bVal = normalizeSearch(getUploadedBy(b));
          return aVal.localeCompare(bVal, 'tr') * dir;
        case 'uploadDate':
        default:
          aVal = new Date(getCreatedDate(a) || 0).getTime();
          bVal = new Date(getCreatedDate(b) || 0).getTime();
          return (aVal - bVal) * dir;
      }
    });

    return list;
  }, [filteredDocuments, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'uploadDate' ? 'desc' : 'asc');
    }
  };

  const SortArrow = ({ column }) => (
    <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.85 }}>
      {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handlePreview = async (docId) => {
    try {
      const result = await documentAPI.download(docId);
      const blob = result?.blob ?? result;
      const mime = result?.contentType || blob?.type;
      const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
      const url = window.URL.createObjectURL(typed);
      window.open(url, '_blank');
    } catch (error) {
      alert('Önizleme açılamadı: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const result = await documentAPI.download(docId);
      const blob = result?.blob ?? result;
      await downloadBlob(blob, result?.fileName || fileName || 'belge', result?.contentType || null);
    } catch (error) {
      alert('İndirme başarısız: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (docId) => {
    if (!await onayla('Bu belgeyi silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      await documentAPI.delete(docId);
      await loadDocuments();
    } catch (error) {
      alert('Silme başarısız: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="makbuzlar-page">
      <div className="page-header">
        <h1>İbraname - Muvafakatname</h1>
      </div>

      <div className="filters-container" style={{ marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="ibraname-search">Arama</label>
          <input
            id="ibraname-search"
            type="search"
            className="form-control"
            placeholder="Müvekkil adı, belge adı veya föy no ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <div className="documents-table-container">
          <div className="table-info">
            <span>
              Toplam: {sortedDocuments.length} belge
              {searchTerm.trim() ? ` (filtre: ${documents.length} içinden)` : ''}
              {' · '}Sıralama: sütun başlığına tıklayarak değiştirin
            </span>
          </div>
          <table className="documents-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('foyNo')}>
                  Föy No <SortArrow column="foyNo" />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('muvekkil')}>
                  Müvekkil <SortArrow column="muvekkil" />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('davaTuru')}>
                  Dosya Türü <SortArrow column="davaTuru" />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('fileName')}>
                  Belge Adı <SortArrow column="fileName" />
                </th>
                <th>Boyut</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('uploadDate')}>
                  Yükleme Tarihi <SortArrow column="uploadDate" />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('uploadedBy')}>
                  Yükleyen <SortArrow column="uploadedBy" />
                </th>
                <th>Açıklama</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocuments.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                    {documents.length === 0
                      ? 'Henüz ibraname / muvafakatname bulunmamaktadır.'
                      : 'Arama kriterine uygun belge bulunamadı.'}
                  </td>
                </tr>
              ) : (
                sortedDocuments.map((doc) => {
                  const docId = doc.Id ?? doc.id;
                  const fileName = getFileName(doc) || 'Bilinmiyor';
                  const fileSize = doc.FileSize ?? doc.fileSize ?? 0;
                  const createdDate = getCreatedDate(doc);
                  const uploadedBy = getUploadedBy(doc) || 'Bilinmiyor';
                  const docDescription = getDescription(doc);
                  const caseFileId = doc.CaseFileId ?? doc.caseFileId;
                  const foy = getFoy(doc) || '-';
                  const muvekkil = getMuvekkil(doc) || '-';
                  const davaLabel = getDavaTuruLabel(doc) || '-';

                  return (
                    <tr key={docId}>
                      <td>
                        {caseFileId ? (
                          <Link to={`/case-files/${caseFileId}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {foy}
                          </Link>
                        ) : (
                          foy
                        )}
                      </td>
                      <td>{muvekkil}</td>
                      <td>{davaLabel}</td>
                      <td>{fileName}</td>
                      <td>{formatFileSize(fileSize)}</td>
                      <td>{createdDate ? formatDateForDisplay(createdDate) : '-'}</td>
                      <td>{uploadedBy}</td>
                      <td>{docDescription || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handlePreview(docId)}
                            title="Önizleme"
                          >
                            👁️
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDownload(docId, fileName)}
                            title="İndir"
                          >
                            ⬇️
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(docId)}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IbranameMuvafakatnameler;
